import React, { useEffect, useRef } from 'react';
import {
    X,
    Copy,
    CheckCircle2,
    AlertCircle,
    XCircle
} from 'lucide-react';

const LogDetailModal = ({ isOpen, onClose, log }) => {
    const modalRef = useRef(null);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen || !log) return null;

    const getStatusIcon = (status) => {
        const lowerStatus = String(status).toLowerCase();
        if (lowerStatus === 'success' || (Number(status) >= 200 && Number(status) < 300))
            return <div className="flex items-center gap-1.5 text-green-600 font-bold text-xs"><CheckCircle2 className="w-4 h-4" /> SUCCESS</div>;
        if (lowerStatus === 'warning' || Number(status) === 429)
            return <div className="flex items-center gap-1.5 text-orange-500 font-bold text-xs"><AlertCircle className="w-4 h-4" /> WARNING</div>;
        return <div className="flex items-center gap-1.5 text-red-600 font-bold text-xs"><XCircle className="w-4 h-4" /> ERROR</div>;
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(JSON.stringify(log, null, 2));
        // You could add a toast notification here
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div
                ref={modalRef}
                className="bg-white w-full max-w-2xl max-h-[90vh] rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
                    <h2 className="text-xl font-bold text-gray-900">Log Details</h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-900"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8">
                    {/* Basic Info Section */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Log Details</h3>
                        <div className="grid grid-cols-2 gap-y-6 gap-x-8">
                            <div>
                                <label className="text-xs font-medium text-gray-400 block mb-1">Timestamp</label>
                                <div className="text-[13px] font-semibold text-gray-900 flex items-center gap-2">
                                    {new Date(log.createdAt).toLocaleString()}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-400 block mb-1">Status</label>
                                {getStatusIcon(log.status)}
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-400 block mb-1">Endpoint</label>
                                <div className="text-[13px] font-semibold text-gray-900 font-mono">
                                    {(log.endpoint || log?.successDetails?.apiEndpoint || '')
                                        .replace(/^https?:\/\/[^/]+/, '')
                                        .replace(/^\/ttt\/v1/, '')
                                        .replace(/^\/api\/v1/, '') || '/'}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-400 block mb-1">Response Time</label>
                                <div className="text-[13px] font-bold text-gray-900">
                                    {log.responseTime >= 1000 ? `${(log.responseTime / 1000).toFixed(2)}s` : `${log.responseTime}ms`}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-400 block mb-1">Tokens Used</label>
                                <div className="text-[13px] font-bold text-gray-900">
                                    {log.tokensUsed || 0}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-400 block mb-1">User ID</label>
                                <div className="text-[13px] font-mono text-gray-900 truncate" title={log.userId}>
                                    {log.userId}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Translation Details Section */}
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Translation Details</h3>
                        <div className="grid grid-cols-2 gap-8">
                            <div>
                                <label className="text-xs font-medium text-gray-400 block mb-1">Source Language</label>
                                <div className="text-[13px] font-bold text-gray-900">{log.sourceLang || 'Detect Language'}</div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-400 block mb-1">Target Language</label>
                                <div className="text-[13px] font-bold text-gray-900">{log.targetLang || 'N/A'}</div>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-medium text-gray-400 block mb-1 font-bold">Input Text</label>
                                <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-700 min-h-[40px] border border-gray-100 leading-relaxed font-medium">
                                    {log.input || 'No input content'}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-400 block mb-1 font-bold">Output Text</label>
                                <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-700 min-h-[40px] border border-gray-100 leading-relaxed font-medium">
                                    {log.output || 'No output content'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Raw Log Data Section */}
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Raw Log Data</h3>
                            <button
                                onClick={handleCopy}
                                className="flex items-center gap-1.5 text-xs font-bold text-[#075038] hover:underline"
                            >
                                <Copy className="w-3.5 h-3.5" />
                                Copy JSON
                            </button>
                        </div>
                        <div className="bg-gray-900 rounded-2xl p-4 overflow-x-auto max-h-[300px]">
                            <pre className="text-[11px] font-mono text-gray-300 leading-relaxed">
                                {JSON.stringify(log, null, 2)}
                            </pre>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 flex justify-end bg-white">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-slate-700 text-white rounded-xl hover:bg-slate-800 transition-all font-bold text-sm shadow-md"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LogDetailModal;
